<template>
    <RouterView
        v-if="isLoaded"
        :default-reports="listOfReports"
        :custom-reports="mappedCustomReportsList"
        @refresh="loadCustomReports"
    />
</template>

<script type="text/babel">
import { mapActions, mapState, mapMutations } from 'vuex';
import orderBy from 'lodash-es/orderBy';
import mappingState from './common/mappingState';
import { resourceCustomReport, resourceDefaultRecipients } from '../api/resources';
import { getCancelToken } from '../api';
import { ROUTE_NAMES, USER_TYPES, REPORT_TYPES } from '../constants';
import { ifIsNotCanceled } from './common/helpers';

export default {
    mixins: [mappingState],

    data() {
        return {
            reportsList: [
                // this order of report types in navigation should be kept
                {
                    id: 3,
                    name: this.$t('reports.defaultReports.ascomEventReport'),
                    type: 3,
                },
                {
                    id: 5,
                    name: this.$t('reports.defaultReports.exceptionReport'),
                    type: 5,
                },
                {
                    id: 2,
                    name: this.$t('reports.defaultReports.fullEventReport'),
                    type: 2,
                },
                {
                    id: 4,
                    name: this.$t('reports.defaultReports.pairEventReport'),
                    type: 4,
                },
                {
                    id: 1,
                    name: this.$t('reports.defaultReports.locationReport'),
                    type: 1,
                },
                {
                    id: 6,
                    name: this.$t('reports.defaultReports.staffReport'),
                    type: 6,
                },
            ],
            customReportsList: [],
            isLoaded: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState('user', ['user']),

        listOfReports() {
            return this.user.type === USER_TYPES.USER ? this.reportsList.slice(0, 5) : this.reportsList;
        },

        /**
         * Returns array of custom report objects with type name
         * @returns {Object[]} array of custom report objects
         */
        mappedCustomReportsList() {
            return this.customReportsList.map(r => ({
                ...r,
                typeName: this.reportsList.find(item => item.type === r.type).name,
            }));
        },
    },

    watch: {
        $route(to) {
            this.setMode(to);
            this.updateData(() => {
                this.showLoading(true);
                if (this.$route.name.includes('custom')) {
                    this.$store.commit('SET_UNSAVED_CHANGES', false);
                    return this.$router
                        .push({
                            name: ROUTE_NAMES.REPORTS,
                            params: { id: REPORT_TYPES.ASCOM_EVENT },
                            query: {},
                        })
                        .then(() => this.loadStaff(this.cancelToken.token))
                        .then(() => this.loadCustomReports())
                        .then(() => this.loadDefaultRecipients());
                }
                return this.loadStaff(this.cancelToken.token)
                    .then(() => this.loadCustomReports())
                    .then(() => this.loadDefaultRecipients());
            });
        },
    },

    created() {
        this.setMode(this.$route);
        this.updateData(() => {
            this.showLoading(true);
            if (this.$route.name.includes('custom')) {
                this.$store.commit('SET_UNSAVED_CHANGES', false);
                return this.$router
                    .push({
                        name: ROUTE_NAMES.REPORTS,
                        params: { id: REPORT_TYPES.ASCOM_EVENT },
                    })
                    .then(() =>
                        this.loadStaff(this.cancelToken.token)
                            .then(() => this.loadCustomReports())
                            .then(() => this.loadDefaultRecipients())
                    );
            }
            return this.loadStaff(this.cancelToken.token)
                .then(() => this.loadCustomReports())
                .then(() => this.loadDefaultRecipients());
        });
        this.loadReports();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapActions(['loadStaff', 'setDefaultRecipients']),

        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        loadReports() {
            this.showLoading(true);
            this.isLoaded = false;
            return (this.user.type === USER_TYPES.USER
                ? this.loadCustomReports()
                : // Need to get staff list for the staff performance report
                  this.loadStaff(this.cancelToken.token).then(() => this.loadCustomReports())
            )
                .then(() => this.loadDefaultRecipients())
                .then(() => {
                    this.isLoaded = true;
                    this.showLoading(false);
                })
                .catch(
                    ifIsNotCanceled({
                        callbackAlways: err => {
                            this.isLoaded = true;
                            return err;
                        },
                    })
                );
        },

        /**
         * Loads custom reports from database, put reports in order by name
         * @returns {Promise}
         */
        loadCustomReports() {
            return resourceCustomReport({ cancelToken: this.cancelToken.token })
                .then(response => response.data)
                .then(customReports => {
                    this.customReportsList = [];
                    if (customReports.length !== 0) {
                        if (this.user.type === USER_TYPES.SUPER_USER) {
                            let filtredReports = customReports.filter(
                                rep => rep.configuration.aa[0] === this.user.area
                            );
                            this.customReportsList = orderBy(filtredReports, 'name');
                        } else {
                            this.customReportsList = orderBy(customReports, 'name');
                        }
                    }
                });
        },

        loadDefaultRecipients() {
            return resourceDefaultRecipients({ cancelToken: this.cancelToken.token }).then(response => {
                this.setDefaultRecipients(response.data.emails);
            });
        },

        /**
         * Redirects from initial reports page to page for creation Of Ascom Event Report
         * @param route
         */
        setMode(route) {
            if (route.name === ROUTE_NAMES.REPORTS) {
                this.$router.replace({
                    name: ROUTE_NAMES.REPORTS__EDIT,
                    params: { id: 3 },
                });
            }
        },
    },
};
</script>
